// @flow

// 구독이 놓친 자막을 조회로 채운다 (#182).
// 측정에서 저장은 됐지만 구독으로 오지 않은 메시지가 약 0.4% 있었고, 전부 구독이 막 붙은 직후였다.
// 그래서 (재)구독 직후와 탭이 다시 보일 때 최근 메시지를 조회해 빠진 것을 합친다.

import { API } from "aws-amplify";
import { useEffect, useRef } from "react";
import { messagesBySession } from "../graphql/queries";

// 구독이 실제로 붙는 시점을 알 수 없어(aws-amplify 4.3 기준 연결 상태 이벤트 없음) 두 번 본다
export const BACKFILL_DELAYS_MS = [2000, 6000];
// 이보다 오래된 메시지는 송출 화면에 뒤늦게 띄우지 않는다
export const BROADCAST_STALE_MS = 30000;

type Message = {
  id: string,
  text: string,
  createdAt: string,
  receivedTime?: number,
  ...
};

export function isClearText(text: string): boolean {
  return text === " " || text === "　";
}

function isBlankText(text: string): boolean {
  return text === "" || isClearText(text);
}

function createdAtMs(message: Message): number {
  const t = Date.parse(message.createdAt);
  return Number.isNaN(t) ? 0 : t;
}

function byCreatedAt(a: Message, b: Message): number {
  return createdAtMs(a) - createdAtMs(b);
}

// 오래된 것부터 정렬해 돌려준다
export async function fetchRecentMessages(
  sessionId: string,
  limit: number
): Promise<Array<any>> {
  const apiData = await API.graphql({
    query: messagesBySession,
    variables: { sessionMessagesId: sessionId, sortDirection: "DESC", limit },
  });
  return (apiData.data?.messagesBySession?.items ?? []).slice().reverse();
}

// 히스토리: id로 중복을 없애고 시간순으로 합친다. 빈 줄·지우기 표시는 히스토리에 넣지 않는다
export function mergeHistory<T: Message>(
  prev: Array<T>,
  incoming: Array<T>,
  max: number
): Array<T> {
  const known = new Set(prev.map((m) => m.id));
  const added = incoming.filter((m) => !known.has(m.id) && !isBlankText(m.text));
  if (added.length === 0) {
    return prev;
  }
  return [...prev, ...added].sort(byCreatedAt).slice(-max);
}

// 송출 화면: 지금 떠 있는 줄보다 새로운 메시지만 채운다.
// 놓친 '지우기'가 있으면 그 앞 줄은 내리고, 오래된 메시지는 띄우지 않는다.
export function reconcileBroadcast<T: Message>(
  prev: Array<T>,
  fetched: Array<T>,
  lineNumber: number,
  now: number
): Array<T> {
  const known = new Set(prev.map((m) => m.id));
  const newestShown = prev.reduce((t, m) => Math.max(t, createdAtMs(m)), -Infinity);
  const newer = fetched
    .filter((m) => !known.has(m.id) && createdAtMs(m) > newestShown)
    .sort(byCreatedAt);
  if (newer.length === 0) {
    return prev;
  }

  let lastClear = -1;
  newer.forEach((m, i) => {
    if (isClearText(m.text)) {
      lastClear = i;
    }
  });
  const base = lastClear >= 0 ? [] : prev;
  const toShow = newer
    .slice(lastClear + 1)
    .filter((m) => m.text !== "" && now - createdAtMs(m) < BROADCAST_STALE_MS)
    .map((m) => ({ ...m, receivedTime: now }));

  if (lastClear < 0 && toShow.length === 0) {
    return prev;
  }
  return [...base, ...toShow].slice(-lineNumber);
}

// (재)구독 직후 정해진 시점들과, 탭이 다시 보일 때 backfill을 부른다.
// 반환값 scheduleAfterSubscribe()를 subscribe() 직후에 호출한다.
export function useBackfill(backfill: () => mixed, deps: Array<mixed>): () => void {
  const timers = useRef([]);
  const backfillRef = useRef(backfill);
  backfillRef.current = backfill;

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        backfillRef.current();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return () => {
    timers.current.forEach(clearTimeout);
    timers.current = BACKFILL_DELAYS_MS.map((ms) =>
      setTimeout(() => backfillRef.current(), ms)
    );
  };
}
